import React from 'react'
import { useIntl } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import zIndex from 'styles/tools/z-index'
import { PERSONALITY_DESCRIPTIONS, PERSONALITY_GRADIENTS, PERSONALITY_IMAGE_IDS, PERSONALITY_NAMES, PERSONALITY_PERCENTILES } from './TravelPersonalityStyles'
import Label from 'LuxKit/Label'
import BodyText from 'LuxKit/Typography/BodyText'
import { getImageUrl } from 'lib/image/imageUtils'
import Heading from 'LuxKit/Typography/Heading'
import SparklesAnimation from '../SparklesAnimation'
import SlideBase from './SlideBase'
import Sheet from 'components/utils/Sheet'
import { useWrappedStorySelector } from 'contexts/Wrapped'

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
`

const Content = styled.div`
  position: relative;
  z-index: ${zIndex.min};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(40)};
`

const PersonalityCard = styled(Sheet)`
  position: relative;
  width: 100%;
  max-width: ${rem(327)};
  aspect-ratio: 327 / 446;
  padding: ${rem(24)} ${rem(20)} ${rem(40)};
  border-radius: ${rem(20)};
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(16)};

  &.the-sun-chaser {
    background: ${PERSONALITY_GRADIENTS['the-sun-chaser']};
  }
  &.the-wellness-wanderer {
    background: ${PERSONALITY_GRADIENTS['the-wellness-wanderer']};
  }
  &.the-nature-roamer {
    background: ${PERSONALITY_GRADIENTS['the-nature-roamer']};
  }
  &.the-bond-builder {
    background: ${PERSONALITY_GRADIENTS['the-bond-builder']};
  }
  &.the-romance-storyteller {
    background: ${PERSONALITY_GRADIENTS['the-romance-storyteller']};
  }
  &.the-trend-explorer {
    background: ${PERSONALITY_GRADIENTS['the-trend-explorer']};
  }
  &.the-gourmet-hunter {
    background: ${PERSONALITY_GRADIENTS['the-gourmet-hunter']};
  }
  &.the-elite-traveller {
    background: ${PERSONALITY_GRADIENTS['the-elite-traveller']};
  }
  &.the-curious-explorer {
    background: ${PERSONALITY_GRADIENTS['the-curious-explorer']};
  }
`

const AvatarContainer = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  aspect-ratio: 1;
  animation: ${float} 3s ease-in-out infinite;
  /* GPU acceleration for smooth animation */
  will-change: transform;
  transform: translateZ(0);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    will-change: auto;
  }
`

const SparklesOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 160%;
  height: 160%;
  pointer-events: none;
  z-index: ${zIndex.min};
`

const PersonalityImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(4)};
`

const LottieStyle = { width: '100%', height: '100%' }

function TravelPersonalitySlide() {
  const intl = useIntl()
  // Use granular selector to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)

  if (!data) return null

  const { personality } = data
  const personalityName = PERSONALITY_NAMES[personality.type]
  const words = personalityName.split(' ')
  const firstLine = words.slice(0, 2).join(' ')
  const secondLine = words.slice(2).join(' ')
  const personalityImage = getImageUrl(PERSONALITY_IMAGE_IDS[personality.type], {
    format: 'png',
    width: 190,
  })

  const yearLabel = intl.formatMessage({
    id: 'WX4E8Ukscd',
    defaultMessage: 'IN 2025',
    description: 'Travel personality slide year label',
  })
  const travelStyleHeading = intl.formatMessage({
    id: 'CjO4mQOPCP',
    defaultMessage: 'Your travel style was',
    description: 'Travel personality slide heading',
  })
  const percentileLabel = intl.formatMessage({
    id: 'S74sKbHipD',
    defaultMessage: '{percentile}% of travellers had a style like yours',
    description: 'Travel personality slide percentile label with variable',
  }, {
    percentile: PERSONALITY_PERCENTILES[personality.type],
  })

  return (
    <SlideBase verticalAlign="center">
      <Content>
        <PersonalityCard className={personality.type} paletteType="default">
          {/* <DecoLines /> */}
          <BodyText variant="large"><b>{yearLabel}</b></BodyText>
          <AvatarContainer>
            <PersonalityImage src={personalityImage} alt={personalityName} />
            <SparklesOverlay>
              <SparklesAnimation style={LottieStyle} />
            </SparklesOverlay>
          </AvatarContainer>
          <TitleSection>
            <Heading variant="heading5">{travelStyleHeading}</Heading>
            <Heading variant="heading1alt" align="center">
              {firstLine}<br />{secondLine}
            </Heading>
            <Label kind="primary" variant="dark">{percentileLabel}</Label>
          </TitleSection>
        </PersonalityCard>
        <BodyText variant="large" align="center">{PERSONALITY_DESCRIPTIONS[personality.type]}</BodyText>
      </Content>
    </SlideBase>
  )
}

export default React.memo(TravelPersonalitySlide)
